import { useTitanApp } from "@/hooks/useTitanApp";
import { useEffect, useState } from "react";
import useTitanType from "@/hooks/useTitanType";
import {
  addEmailsAndSmsTemplates,
  updateEmailsAndSmsTemplates,
  getEmailsAndSmsTemplates,
  deleteTemplates,
} from "@/dispatchers/templateDispatchers";

export interface Template {
  id: string;
  name: string;
  type: "email" | "sms";
  subject?: string;
  content: string;
  actionType?: number;
}

interface EmailAndSmsTemplate {
  _id: string;
  subject: string;
  text: string;
  createdOn: string;
  messageType: 0 | 1; // 0 = email, 1 = sms
  templateName: string;
  companyID: string;
  isDefault: boolean;
  actionType: number;
  isActive: boolean;
}

interface TemplateSelectorProps {
  label: string;
  value: string;
  templates: Template[];
  templateType: "email" | "sms";
  actionType: number;
  onSelect: (template: string) => void;
}

export interface TemplatePreviewModalProps {
  template: Template;
  templateType: string;
  onClose: () => void;
}

export const useTemplateSettingsData = () => {
  const { appState, templateState } = useTitanApp();
  const { typeState } = useTitanType();
  const { messageTemplates } = typeState || {};

  const { emailsAndSmsTemplates, isTemplatesLoading } = templateState || {};
  const { darkMode, companyId, currentUser } = appState || {};
  const isDarkMode = darkMode ?? false;
  const [templates, setTemplate] = useState<Template[]>([]);
  const [emailAndSmsTemplates, setEmailAndSmsTemplates] = useState<
    EmailAndSmsTemplate[]
  >([]);

  useEffect(() => {
    if (messageTemplates && Array.isArray(messageTemplates)) {
      setTemplate(
        messageTemplates?.map((s: any) => ({
          id: s._id || "",
          name: s?.templateName || "",
          type: s?.messageType || "",
          subject: s?.subject || "",
          content: s?.text || "",
          actionType: s?.actionType || "",
        })),
      );
    }
  }, [messageTemplates]);
  useEffect(() => {
    if (emailsAndSmsTemplates && Array.isArray(emailsAndSmsTemplates)) {
      setEmailAndSmsTemplates(
        emailsAndSmsTemplates.map((s: any) => ({
          _id: s._id || "",
          subject: s.subject || "",
          text: s.text || "",
          createdOn: s.createdOn || "",
          messageType: s.messageType,
          templateName: s.templateName || "",
          companyID: s.companyID || "",
          isDefault: !!s.isDefault,
          actionType: s.actionType,
          isActive: !!s.isActive,
        })),
      );
    }
  }, [emailsAndSmsTemplates]);

  const addTemplate = async (templateData: Partial<EmailAndSmsTemplate>) => {
    try {
      const res = await addEmailsAndSmsTemplates(templateData);
      await getEmailsAndSmsTemplates(companyId);
      return res;
    } catch (error) {
      throw error; // ← re-throw so handleSave catch block receives it
    }
  };

  const updateTemplate = async (templateData: Partial<EmailAndSmsTemplate>) => {
    try {
      const res = await updateEmailsAndSmsTemplates(templateData);
      await getEmailsAndSmsTemplates(companyId); // refresh
      return res;
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      await deleteTemplates([templateId]); // Pass as array
      // Optionally update local state here
    } catch (error) {
      console.error("Error deleting template:", error);
    }
  };

  return {
    isDarkMode,
    templates,
    emailAndSmsTemplates,
    companyId,
    deleteTemplate,
    addTemplate,
    updateTemplate,
    isTemplatesLoading,
    currentUser,
  };
};
